import get from 'lodash/get';
import { useEffect } from 'react';
import type { GlobalError } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Col } from '@/components/Col';
import { InputErrorMessage } from '@/components/InputErrorMessage';
import { Row } from '@/components/Row';
import type { SelectFieldProps } from '@/components/SelectField';
import { RESET_OPTION, SelectField } from '@/components/SelectField';
import { Typography } from '@/components/Typography';
import { getMonthsByLocale } from '@/lib/dates';
import { CONTENTSQUARE_MASKED } from '@/lib/forms/const';
import { commonMessages } from '@/messages/common';
import { formMessages } from '@/messages/forms';
import { useRegionProvider } from '@/providers/RegionProvider';

interface BirthdateFieldsProps extends Pick<SelectFieldProps, 'colorVariant'> {
  label?: React.ReactNode;
}

export const getDayOptions = () => [
  RESET_OPTION,
  ...Array.from({ length: 31 }, (_, i) => ({
    label: (i + 1).toString(),
    value: (i + 1).toString(),
  })),
];

export const getMonthOptions = (months: string[]) => [
  RESET_OPTION,
  ...months.map((month, i) => ({
    label: month,
    value: i.toString(),
  })),
];

export const getYearOptions = (fromYear = 1900) => [
  RESET_OPTION,
  ...Array.from(
    { length: new Date().getFullYear() - fromYear + 1 },
    (_, i) => ({
      label: (i + fromYear).toString(),
      value: (i + fromYear).toString(),
    }),
  ).reverse(),
];

export const BirthdateFields = ({
  colorVariant,
  label,
}: BirthdateFieldsProps) => {
  const { formatMessage } = useIntl();
  const {
    country,
    locale: { locale },
  } = useRegionProvider();
  const { formState, getValues, trigger } = useFormContext();

  const dobDay = useWatch({ name: 'dobDay' });
  const dobMonth = useWatch({ name: 'dobMonth' });
  const dobYear = useWatch({ name: 'dobYear' });

  const error = (get(formState.errors, 'dobDay') ||
    get(formState.errors, 'dobMonth') ||
    get(formState.errors, 'dobYear')) as GlobalError;

  useEffect(() => {
    if (dobDay && dobMonth && dobYear) {
      trigger(['dobDay', 'dobMonth', 'dobYear']);
    }
  }, [dobDay, dobMonth, dobYear, trigger]);

  const selectedCountryCode = getValues('country')
    ? getValues('country').slug
    : country.code;

  return (
    <Row className={CONTENTSQUARE_MASKED} data-test-id='BirthdateFields'>
      <Col>
        <Typography data-test-id='BirthdateFieldsLabel' mb={1}>
          {label ?? formatMessage(formMessages.birthDate)}
        </Typography>
      </Col>
      <Col>
        <Row data-test-id='BirthdateFieldsRow'>
          <Col
            xs={4}
            {...(selectedCountryCode === 'us'
              ? { xsOrder: 2 }
              : { xsOrder: 1 })}
            data-test-id='BirthdateFieldsDayCol'
          >
            <SelectField
              aria-label={formatMessage(commonMessages.day)}
              colorVariant={colorVariant}
              data-test-id='BirthdateFieldsDaySelect'
              name='dobDay'
              options={getDayOptions()}
              placeholder={formatMessage(commonMessages.day)}
              showError={false}
            />
          </Col>
          <Col
            xs={4}
            {...(selectedCountryCode === 'us'
              ? { xsOrder: 1 }
              : { xsOrder: 2 })}
            data-test-id='BirthdateFieldsMonthCol'
          >
            <SelectField
              aria-label={formatMessage(commonMessages.month)}
              colorVariant={colorVariant}
              data-test-id='BirthdateFieldsMonthSelect'
              name='dobMonth'
              options={getMonthOptions(getMonthsByLocale(locale))}
              placeholder={formatMessage(commonMessages.month)}
              showError={false}
            />
          </Col>
          <Col data-test-id='BirthdateFieldsYearCol' xs={4} xsOrder={3}>
            <SelectField
              aria-label={formatMessage(commonMessages.year)}
              colorVariant={colorVariant}
              data-test-id='BirthdateFieldsYearSelect'
              name='dobYear'
              options={getYearOptions(1900)}
              placeholder={formatMessage(commonMessages.year)}
              showError={false}
            />
          </Col>
        </Row>
      </Col>
      <Col>
        {dobDay && dobMonth && dobYear && (
          <InputErrorMessage
            data-test-id='BirthdateFieldsError'
            error={error}
          />
        )}
      </Col>
    </Row>
  );
};
